import React, { useCallback } from "react"; // Import React and useCallback hook
import Particles from "@tsparticles/react"; // Import the Particles component for particle backgrounds
import { loadSlim } from "@tsparticles/slim"; // Import the loadSlim preset for lightweight tsParticles

// Define a functional component for the particle background
const ParticleBackground = () => {
  // Memoized callback to initialize particles with the slim engine preset.
  const particlesInit = useCallback(async (engine) => {
    // Loads the slim configuration for tsParticles engine (smaller bundle size).
    await loadSlim(engine);
  }, []);

  // Memoized callback that runs after particles have loaded (optional usage).
  const particlesLoaded = useCallback(async (container) => {
    // Placeholder for actions on load, e.g., you could log or perform actions here.
  }, []);

  // Define the configuration object for particle options.
  const particlesConfig = {
    background: {
      // Set background opacity to 0 (fully transparent background).
      opacity: 0,
    },
    fpsLimit: 120, // Limit the max FPS to 120 for smoother animation.
    interactivity: {
      // Settings for how users can interact with the particles.
      events: {
        onClick: {
          enable: true, // Enable interaction on click.
          mode: "push", // On click, add new particles.
        },
        onHover: {
          enable: true, // Enable interaction on hover.
          mode: "repulse", // On hover, push particles away.
        },
        resize: true, // Redraw particles when window resizes.
      },
      modes: {
        push: {
          quantity: 4, // When "push" mode, add 4 particles per click.
        },
        repulse: {
          distance: 200, // Repulse particles within 200px.
          duration: 0.4, // How long the repulse effect lasts (seconds).
        },
      },
    },
    particles: {
      color: {
        value: "#ff014f", // Base color of particles.
      },
      links: {
        color: "#ff014f", // Link color.
        distance: 150, // Maximum distance between particles to form a link.
        enable: true, // Enable drawing lines between particles.
        opacity: 0.3, // Opacity of the link lines.
        width: 1, // Line width in pixels.
      },
      collisions: {
        enable: true, // Collide particles with each other.
      },
      move: {
        direction: "none", // No preferred direction; move randomly.
        enable: true, // Particles are animated (move).
        outModes: {
          default: "bounce", // Bounce on container edges.
        },
        random: false, // Don’t randomize movement direction per particle.
        speed: 2, // Particle speed (pixels/frame).
        straight: false, // Do not move strictly straight.
      },
      number: {
        density: {
          enable: true, // Enable density to auto-adjust amount based on area.
          area: 800, // Area of density calculation.
        },
        value: 80, // Start with 80 particles.
      },
      opacity: {
        value: 0.5, // Particle opacity.
      },
      shape: {
        type: "circle", // Shape of each particle.
      },
      size: {
        value: { min: 1, max: 3 }, // Size range for particles.
      },
    },
    detectRetina: true, // Enable retina display support.
  };

  // Render the Particles component with configuration and callbacks.
  return (
    <Particles
      id="tsparticles" // HTML element id for the canvas.
      init={particlesInit} // Particle system init callback.
      loaded={particlesLoaded} // Particle system loaded callback.
      options={particlesConfig} // Main options for configuring the system.
      className="absolute inset-0 w-full h-full" // Utility classes (Tailwind CSS in this case) for full-screen coverage.
    />
  );
};

export default ParticleBackground; // Export component for use in other files.
